/**
 * Report Access — screen 08, ported from the validated mockup.
 *
 * Grant a report to a company and everyone in it inherits it; a per-person exception beats
 * the company grant in both directions. That is `resolveReportAccess`, and this screen is
 * the only place it is edited.
 *
 * ⚠ **The "Reach" column comes from the SERVER**, which computes it by running
 * `resolveReportAccess` over the real user list — the same call the catalogue and the
 * report endpoints make. The mockup worked it out in the page, which was right for a
 * mockup and would be a second implementation of the access rule here. Two implementations
 * of "who may see this" drift, and the first sign of the drift is a contractor being shown
 * a card that 403s — or, far worse, one the matrix says they cannot have.
 *
 * ⚠ Nothing here is scoped to rows. A grant decides whether somebody can OPEN a report;
 * what they then see inside it is `contractorScope`, which no screen can override. Said on
 * the page, because "grant Circet the order activation report" sounds like it might mean
 * "let Circet see the orders", and it does not mean that.
 */

import { useCallback, useEffect, useState } from "react";

import type { ReportAccessRow, ReportException } from "@cocon-stats/shared";
import { AlertTriangle, CheckCircle, Inbox, PlusCircle, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/lib/api-client";

interface AccessBoard {
  reports: ReportAccessRow[];
  exceptions: ReportException[];
  companies: Array<{ id: string; code: string; name: string; kind: "INTERNAL" | "CONTRACTOR" }>;
  people: Array<{ id: string; email: string; displayName: string; companyName: string }>;
}

export function ReportAccess() {
  const [board, setBoard] = useState<AccessBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [personId, setPersonId] = useState("");
  const [reportKey, setReportKey] = useState("");
  const [effect, setEffect] = useState<"ALLOW" | "DENY">("ALLOW");

  useEffect(() => {
    apiFetch<AccessBoard>("/admin/access")
      .then((data) => {
        setBoard(data);
        setPersonId(data.people[0]?.id ?? "");
        setReportKey(data.reports[0]?.key ?? "");
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (savedAt === null) return;
    const timer = setTimeout(() => setSavedAt(null), 1800);
    return () => clearTimeout(timer);
  }, [savedAt]);

  /** Every write returns the whole board, so the reach figures never go stale. */
  const save = useCallback(async (path: string, body: unknown) => {
    setBusy(true);
    try {
      setBoard(await apiFetch<AccessBoard>(path, { method: "PUT", body: JSON.stringify(body) }));
      setSavedAt(Date.now());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "The change could not be saved.");
    } finally {
      setBusy(false);
    }
  }, []);

  if (loading) return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;

  if (failed || !board) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">Report access could not be loaded</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Reload the page, or try again shortly.</p>
        </div>
      </div>
    );
  }

  const contractors = board.companies.filter((c) => c.kind === "CONTRACTOR");
  const internal = board.companies.filter((c) => c.kind === "INTERNAL");

  return (
    <div className="max-w-[1100px] space-y-6 px-4 py-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <div className="gradient-band flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-slate-900">Who sees which report</h2>
          <p className="text-sm text-slate-500">
            Grant a report to a company and everyone in it inherits access. Rows are always filtered to their own
            company.
          </p>
        </div>
        {savedAt !== null && (
          <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#047857" }}>
            <CheckCircle className="h-3.5 w-3.5" /> Saved
          </span>
        )}
      </div>

      <section className="card overflow-hidden">
        <div className="gradient-band px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-white">
          Company Grants
        </div>
        <div className="custom-scrollbar overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ minWidth: 240 }}>Report</th>
                {contractors.map((company) => (
                  <th key={company.id} className="text-center" style={{ width: 150 }}>
                    {company.name}
                  </th>
                ))}
                {internal.map((company) => (
                  <th key={company.id} className="text-center" style={{ width: 150 }}>
                    {company.name}
                  </th>
                ))}
                <th style={{ width: 170 }}>Reach</th>
              </tr>
            </thead>
            <tbody>
              {board.reports.map((report) => (
                <tr key={report.key}>
                  <td>
                    <p className="text-sm font-semibold text-slate-800">{report.name}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {report.category}
                      {report.isReady ? "" : " · awaiting spec"}
                    </p>
                  </td>
                  {contractors.map((company) => {
                    const grant = report.companyGrants[company.id];
                    return (
                      <td key={company.id} className="text-center">
                        <input
                          type="checkbox"
                          className="tick mx-auto"
                          checked={grant === "ALLOW"}
                          disabled={busy}
                          aria-label={`${report.name} for ${company.name}`}
                          onChange={(e) =>
                            void save("/admin/access/company", {
                              reportKey: report.key,
                              companyId: company.id,
                              // ⚠ Unticking REMOVES the grant rather than storing a DENY.
                              // Both resolve to "no", but the audit log has to be able to
                              // tell "nobody ever granted this" from "somebody took it
                              // away", and only one of those is a decision.
                              effect: e.target.checked ? "ALLOW" : null,
                            })
                          }
                        />
                        {grant === "DENY" && (
                          <span className="pill pill-red mt-1" title="A company-level block is set on this report">
                            Blocked
                          </span>
                        )}
                      </td>
                    );
                  })}
                  {internal.map((company) => (
                    <td key={company.id} className="text-center">
                      {/* ⚠ Ticked and disabled, exactly as the mockup has it. An internal
                          role resolves to "visible" before any grant is consulted, so a row
                          here would be a second mechanism saying the same thing — and the
                          first one to drift. */}
                      <input
                        type="checkbox"
                        className="tick mx-auto"
                        checked
                        disabled
                        aria-label={`${report.name} for ${company.name}`}
                        title={`${company.name} is internal and always has access`}
                      />
                    </td>
                  ))}
                  <td className="text-xs text-slate-600">
                    <span className="num font-semibold text-slate-800">{report.reach}</span> people
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="gradient-band flex items-center justify-between px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Exceptions</span>
          <span className="text-xs text-white/85">
            {board.exceptions.length} {board.exceptions.length === 1 ? "exception" : "exceptions"}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-[200px] flex-1 space-y-1.5">
            <label className="eyebrow">Person</label>
            <select className="field" value={personId} onChange={(e) => setPersonId(e.target.value)}>
              {board.people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName} — {person.email}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[180px] flex-1 space-y-1.5">
            <label className="eyebrow">Report</label>
            <select className="field" value={reportKey} onChange={(e) => setReportKey(e.target.value)}>
              {board.reports.map((report) => (
                <option key={report.key} value={report.key}>
                  {report.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full space-y-1.5 sm:w-40">
            <label className="eyebrow">Effect</label>
            <select className="field" value={effect} onChange={(e) => setEffect(e.target.value as "ALLOW" | "DENY")}>
              <option value="ALLOW">Grant</option>
              <option value="DENY">Block</option>
            </select>
          </div>
          <button
            type="button"
            className="btn btn-outline"
            disabled={busy || !personId || !reportKey}
            onClick={() => void save("/admin/access/user", { reportKey, userId: personId, effect })}
          >
            <PlusCircle className="h-4 w-4" /> Add
          </button>
        </div>

        {/* ⚠ Only contractors are offered. An internal role is granted every report before
            grants are read, so an exception naming one would sit here doing nothing — in
            either direction, including a block, which is the dangerous misreading. */}
        {board.people.length === 0 && (
          <p className="px-5 py-3 text-xs text-slate-400">
            Nobody outside Unifiber has an account yet, so there is nobody to make an exception for.
          </p>
        )}

        <table className="tbl">
          <thead>
            <tr>
              <th>Person</th>
              <th>Company</th>
              <th>Report</th>
              <th>Effect</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {board.exceptions.map((exception) => (
              <tr key={`${exception.userId}-${exception.reportKey}`}>
                <td>
                  <p className="text-sm font-semibold text-slate-800">{exception.displayName}</p>
                  <p className="text-xs text-slate-400">{exception.email}</p>
                </td>
                <td className="text-slate-700">{exception.companyName}</td>
                <td className="text-slate-700">{exception.reportName}</td>
                <td>
                  {exception.effect === "ALLOW" ? (
                    <span className="pill pill-emerald">Granted</span>
                  ) : (
                    <span className="pill pill-red">Blocked</span>
                  )}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Remove this exception — they go back to following their company"
                    disabled={busy}
                    onClick={() =>
                      void save("/admin/access/user", {
                        reportKey: exception.reportKey,
                        userId: exception.userId,
                        effect: null,
                      })
                    }
                  >
                    <X className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {board.exceptions.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center">
                  <Inbox className="mx-auto h-6 w-6 text-slate-300" />
                  <p className="mt-3 text-sm text-slate-500">No exceptions. Everyone follows their company grant.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="card flex items-start gap-3 p-4" style={{ borderLeft: "4px solid #f59e0b" }}>
        <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: "#b45309" }} />
        <div className="space-y-1 text-sm text-slate-700">
          <p>An exception always wins over the company grant. A block takes effect on their next page load.</p>
          <p className="text-xs text-slate-500">
            {/* ⚠ The sentence that stops the dangerous misreading. "Grant Circet the Order
                Activation report" sounds like it might mean "let Circet see the orders". It
                does not: it lets them open the report, where they see their own rows in the
                drawings Ubi has published, and nothing else. */}
            A grant decides whether somebody can <span className="font-semibold">open</span> a report. What they see
            inside it never changes: their own company&rsquo;s rows, in the drawings that have been opened to them.
          </p>
        </div>
      </div>
    </div>
  );
}
